import React, { useCallback, useEffect, useState } from "react";
import { Film } from "lucide-react";
import { toast } from "sonner";
import { adminApi, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { Btn, Select, panel } from "@/admin/kit";

const Row = ({ video, releases, onAttached }) => {
  const [rid, setRid] = useState("");
  const [busy, setBusy] = useState(false);
  const attach = async () => {
    if (!rid) return toast.error("Pick a release first");
    setBusy(true);
    try {
      await adminApi.post(`/admin/videos/${video.id}/attach`, { release_id: rid });
      toast.success("Video attached — open the release to review it");
      onAttached();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="p-3 flex flex-wrap items-center gap-4" style={panel} data-testid={`unassigned-video-${video.id}`}>
      <div className="w-12 h-12 grid place-items-center overflow-hidden shrink-0" style={{ background: "#000", borderRadius: "var(--radius)" }}>
        {video.poster_url ? <img src={mediaUrl(video.poster_url)} alt="" className="w-full h-full object-cover" /> : <Film size={16} color="var(--ash)" />}
      </div>
      <div className="flex-1 min-w-[160px]">
        <p className="truncate" style={{ color: "var(--parchment)" }}>{video.title}</p>
        <p style={{ color: "var(--ash)", fontSize: ".7rem" }}>{video.source_type === "upload" ? "Uploaded file" : `${video.source_type} link`} · {video.status}</p>
      </div>
      <Select value={rid} onChange={(e) => setRid(e.target.value)} style={{ maxWidth: 260 }} data-testid={`unassigned-pick-${video.id}`}>
        <option value="">Attach to release…</option>
        {releases.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
      </Select>
      <Btn type="button" variant="ghost" onClick={attach} disabled={busy || !rid} data-testid={`unassigned-attach-${video.id}`}>{busy ? "Attaching…" : "Attach"}</Btn>
    </div>
  );
};

export const UnassignedVideos = ({ releases = [], onAttached }) => {
  const [videos, setVideos] = useState([]);
  const [all, setAll] = useState(releases);
  const load = useCallback(() => {
    adminApi.get("/admin/videos/unassigned").then(({ data }) => setVideos(data)).catch(() => setVideos([]));
    adminApi.get("/admin/releases").then(({ data }) => setAll(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  if (videos.length === 0) return null;
  return (
    <div className="mt-10" data-testid="unassigned-videos">
      <div className="flex items-center gap-2 mb-2">
        <Film size={14} color="var(--gold)" />
        <h2 className="font-display foil-text" style={{ fontSize: "1.3rem" }}>Unassigned videos ({videos.length})</h2>
      </div>
      <p className="mb-4" style={{ color: "var(--ash)", fontSize: ".75rem" }}>
        These came from the old Videos section and aren&apos;t linked to a release yet. Nothing was deleted — attach each one to the release it belongs to and it becomes that release&apos;s music video.
      </p>
      <div className="grid gap-3">
        {videos.map((v) => <Row key={v.id} video={v} releases={all} onAttached={() => { load(); onAttached?.(); }} />)}
      </div>
    </div>
  );
};
